import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Small } from "@/components/ui/typography";
import { Skeleton } from "@/components/ui/skeleton";
import { Pencil } from "lucide-react";
import isEqual from "fast-deep-equal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  DEFAULT_PUBLIC_QUOTE_TEMPLATE,
  QUOTE_TEMPLATE_METADATA,
  QuoteTemplateId,
  CustomFieldsSchema,
  QuotePriceRounding,
  Permission,
  QuoteAdvancedSettingsSchema,
  DEFAULT_QUOTE_ADVANCED_SETTINGS,
  formatQuoteNumber,
  DEFAULT_ITEM_OVERRIDE_FIELD_SELECTION,
  getCustomFieldNames,
  type ItemOverrideFieldSelection,
  type QuoteAdvancedSettings,
} from "@forgepoint/shared";
import { Input } from "@/components/ui/input";
import { useHasPermission } from "@/store/authStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { CustomFieldsSchemaEditor } from "@/components/settings/CustomFieldsSchemaEditor";
import { AdvancedSettingsSection } from "@/components/settings/AdvancedSettingsSection";
import { SettingLimitInput } from "@/components/settings/SettingLimitInput";
import { DefaultQuoteTemplatePicker } from "@/components/settings/DefaultQuoteTemplatePicker";
import { ItemOverrideFieldToggles } from "@/components/settings/ItemOverrideFieldToggles";
import { PriceBreakAggregationChangeDialog } from "@/components/settings/PriceBreakAggregationChangeDialog";
import { PriceListBreakGroupFieldRow } from "@/components/settings/PriceListBreakGroupFieldRow";
import { QuotePriceRoundingRow } from "@/components/settings/QuotePriceRoundingRow";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { TextSettingSheet } from "@/modules/standardAgent/components/TextSettingSheet";
import { UnsavedChangesBar } from "@/modules/standardAgent/components/UnsavedChangesBar";
import { toast } from "sonner";

interface QuoteSettingsPanelProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
}

/**
 * Quote settings panel for configuring default terms, templates, and display options.
 * Uses the standard settings UX pattern with section labels, settings rows, and sheets.
 * @param props - QuoteSettingsPanelProps
 * @returns Quote settings panel element
 */
export function QuoteSettingsPanel({ isAdmin }: QuoteSettingsPanelProps) {
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const lockedBy = useQuoteSettingsStore((state) => state.lockedBy);
  const isLoading = useQuoteSettingsStore((state) => state.isLoading);
  const error = useQuoteSettingsStore((state) => state.error);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  const updateQuoteSettings = useQuoteSettingsStore(
    (state) => state.updateSettings,
  );
  const itemCustomFieldsSchema = useItemSettingsStore(
    (state) => state.settings?.customFieldsSchema ?? null,
  );
  const enableAllowance = useItemSettingsStore(
    (state) => state.settings?.enableAllowance ?? false,
  );
  const fetchItemSettings = useItemSettingsStore(
    (state) => state.fetchSettings,
  );
  const quoteDocumentsEnabled = useFeatureFlag("quote-documents");
  const canEditSettings = useHasPermission(Permission.SETTINGS_EDIT);

  // ── Form state ──
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [deliveryTerms, setDeliveryTerms] = useState("");
  const [quoteFooter, setQuoteFooter] = useState("");
  const [publicQuoteTemplate, setPublicQuoteTemplate] =
    useState<QuoteTemplateId>(DEFAULT_PUBLIC_QUOTE_TEMPLATE);
  const [quotePriceRounding, setQuotePriceRounding] =
    useState<QuotePriceRounding | null>(null);
  const [useSignatures, setUseSignatures] = useState(false);
  const [showTotals, setShowTotals] = useState(true);
  const [showTotalWeight, setShowTotalWeight] = useState(false);
  const [aggregatePriceListBreaks, setAggregatePriceListBreaks] =
    useState(false);
  const [priceListBreakGroupField, setPriceListBreakGroupField] = useState<
    string | null
  >(null);
  const [isRepriceDialogOpen, setIsRepriceDialogOpen] = useState(false);
  const [showConfidenceScore, setShowConfidenceScore] = useState(false);
  const [enableCoverLetter, setEnableCoverLetter] = useState(false);
  const [showLocationField, setShowLocationField] = useState(false);
  const [allowItemFieldOverrides, setAllowItemFieldOverrides] = useState(false);
  const [itemOverrideFields, setItemOverrideFields] =
    useState<ItemOverrideFieldSelection>(DEFAULT_ITEM_OVERRIDE_FIELD_SELECTION);
  const [allowLineUnitOverride, setAllowLineUnitOverride] = useState(false);
  const [allowPerQuoteDocumentEditing, setAllowPerQuoteDocumentEditing] =
    useState(false);
  const [defaultQuoteDocumentTemplateId, setDefaultQuoteDocumentTemplateId] =
    useState<string | null>(null);
  const [customFieldsSchema, setCustomFieldsSchema] =
    useState<CustomFieldsSchema | null>(null);
  const [lineItemCustomFieldsSchema, setLineItemCustomFieldsSchema] =
    useState<CustomFieldsSchema | null>(null);
  const [quoteNumberPadWidth, setQuoteNumberPadWidth] = useState(0);
  const [quoteNumberPrefix, setQuoteNumberPrefix] = useState("");
  const [advancedSettings, setAdvancedSettings] =
    useState<QuoteAdvancedSettings>(DEFAULT_QUOTE_ADVANCED_SETTINGS);

  // ── UI state ──
  const [isSaving, setIsSaving] = useState(false);
  const [termsSheetOpen, setTermsSheetOpen] = useState(false);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [deliverySheetOpen, setDeliverySheetOpen] = useState(false);
  const [footerSheetOpen, setFooterSheetOpen] = useState(false);
  // ── Derived state ──
  const selectedTemplate = useMemo(
    () =>
      QUOTE_TEMPLATE_METADATA.find(
        (template) => template.id === publicQuoteTemplate,
      ),
    [publicQuoteTemplate],
  );
  const groupableItemFieldNames = useMemo(
    () =>
      getCustomFieldNames(itemCustomFieldsSchema).filter(
        (name) => itemCustomFieldsSchema?.properties[name]?.type[0] !== "array",
      ),
    [itemCustomFieldsSchema],
  );
  const effectiveGroupField = groupableItemFieldNames.find(
    (name) => name === priceListBreakGroupField,
  );

  const isDirty = useMemo(() => {
    if (!quoteSettings) {
      return (
        termsAndConditions !== "" ||
        paymentTerms !== "" ||
        deliveryTerms !== "" ||
        quoteFooter !== "" ||
        publicQuoteTemplate !== DEFAULT_PUBLIC_QUOTE_TEMPLATE ||
        quotePriceRounding !== null ||
        useSignatures !== false ||
        showTotals !== true ||
        showTotalWeight !== false ||
        aggregatePriceListBreaks !== false ||
        priceListBreakGroupField !== null ||
        showConfidenceScore !== false ||
        enableCoverLetter !== false ||
        showLocationField !== false ||
        allowItemFieldOverrides !== false ||
        !isEqual(itemOverrideFields, DEFAULT_ITEM_OVERRIDE_FIELD_SELECTION) ||
        allowLineUnitOverride !== false ||
        allowPerQuoteDocumentEditing !== false ||
        defaultQuoteDocumentTemplateId !== null ||
        customFieldsSchema !== null ||
        lineItemCustomFieldsSchema !== null ||
        quoteNumberPadWidth !== 0 ||
        quoteNumberPrefix !== "" ||
        !isEqual(advancedSettings, DEFAULT_QUOTE_ADVANCED_SETTINGS)
      );
    }

    if (termsAndConditions !== (quoteSettings.termsAndConditions || "")) {
      return true;
    }
    if (paymentTerms !== (quoteSettings.paymentTerms || "")) {
      return true;
    }
    if (deliveryTerms !== (quoteSettings.deliveryTerms || "")) {
      return true;
    }
    if (quoteFooter !== (quoteSettings.quoteFooter || "")) {
      return true;
    }
    if (
      publicQuoteTemplate !==
      (quoteSettings.publicQuoteTemplate || DEFAULT_PUBLIC_QUOTE_TEMPLATE)
    ) {
      return true;
    }
    if (
      JSON.stringify(quotePriceRounding) !==
      JSON.stringify(quoteSettings.quotePriceRounding ?? null)
    ) {
      return true;
    }
    if (useSignatures !== (quoteSettings.useSignatures ?? false)) {
      return true;
    }
    if (showTotals !== (quoteSettings.showTotals ?? true)) {
      return true;
    }
    if (showTotalWeight !== (quoteSettings.showTotalWeight ?? false)) {
      return true;
    }
    if (
      aggregatePriceListBreaks !==
      (quoteSettings.aggregatePriceListBreaks ?? false)
    ) {
      return true;
    }
    if (
      priceListBreakGroupField !==
      (quoteSettings.priceListBreakGroupField ?? null)
    ) {
      return true;
    }
    if (showConfidenceScore !== (quoteSettings.showConfidenceScore ?? false)) {
      return true;
    }
    if (enableCoverLetter !== (quoteSettings.enableCoverLetter ?? false)) {
      return true;
    }
    if (showLocationField !== (quoteSettings.showLocationField ?? false)) {
      return true;
    }
    if (
      allowItemFieldOverrides !==
      (quoteSettings.allowItemFieldOverrides ?? false)
    ) {
      return true;
    }
    if (!isEqual(itemOverrideFields, quoteSettings.itemOverrideFields)) {
      return true;
    }
    if (
      allowLineUnitOverride !== (quoteSettings.allowLineUnitOverride ?? false)
    ) {
      return true;
    }
    if (
      allowPerQuoteDocumentEditing !==
      (quoteSettings.allowPerQuoteDocumentEditing ?? false)
    ) {
      return true;
    }
    if (
      defaultQuoteDocumentTemplateId !==
      (quoteSettings.defaultQuoteDocumentTemplateId ?? null)
    ) {
      return true;
    }
    if (
      JSON.stringify(customFieldsSchema) !==
      JSON.stringify(quoteSettings.customFieldsSchema ?? null)
    ) {
      return true;
    }
    if (
      JSON.stringify(lineItemCustomFieldsSchema) !==
      JSON.stringify(quoteSettings.lineItemCustomFieldsSchema ?? null)
    ) {
      return true;
    }
    if (quoteNumberPadWidth !== (quoteSettings.quoteNumberPadWidth ?? 0)) {
      return true;
    }
    if (quoteNumberPrefix !== (quoteSettings.quoteNumberPrefix ?? "")) {
      return true;
    }
    if (
      !isEqual(
        advancedSettings,
        QuoteAdvancedSettingsSchema.parse(quoteSettings),
      )
    ) {
      return true;
    }

    return false;
  }, [
    quoteSettings,
    termsAndConditions,
    paymentTerms,
    deliveryTerms,
    quoteFooter,
    publicQuoteTemplate,
    quotePriceRounding,
    useSignatures,
    showTotals,
    showTotalWeight,
    aggregatePriceListBreaks,
    priceListBreakGroupField,
    showConfidenceScore,
    enableCoverLetter,
    showLocationField,
    allowItemFieldOverrides,
    itemOverrideFields,
    allowLineUnitOverride,
    allowPerQuoteDocumentEditing,
    defaultQuoteDocumentTemplateId,
    customFieldsSchema,
    lineItemCustomFieldsSchema,
    quoteNumberPadWidth,
    quoteNumberPrefix,
    advancedSettings,
  ]);

  // ── Effects ──
  useEffect(() => {
    fetchQuoteSettings();
    fetchItemSettings();
  }, [fetchQuoteSettings, fetchItemSettings]);

  /**
   * Resets all form fields to match the current stored settings.
   * Used on initial load and when discarding unsaved changes.
   */
  const syncFormFromSettings = useCallback(() => {
    if (!quoteSettings) {
      return;
    }
    setTermsAndConditions(quoteSettings.termsAndConditions || "");
    setPaymentTerms(quoteSettings.paymentTerms || "");
    setDeliveryTerms(quoteSettings.deliveryTerms || "");
    setQuoteFooter(quoteSettings.quoteFooter || "");
    setPublicQuoteTemplate(
      quoteSettings.publicQuoteTemplate || DEFAULT_PUBLIC_QUOTE_TEMPLATE,
    );
    setQuotePriceRounding(quoteSettings.quotePriceRounding ?? null);
    setUseSignatures(quoteSettings.useSignatures ?? false);
    setShowTotals(quoteSettings.showTotals ?? true);
    setShowTotalWeight(quoteSettings.showTotalWeight ?? false);
    setAggregatePriceListBreaks(
      quoteSettings.aggregatePriceListBreaks ?? false,
    );
    setPriceListBreakGroupField(quoteSettings.priceListBreakGroupField ?? null);
    setShowConfidenceScore(quoteSettings.showConfidenceScore ?? false);
    setEnableCoverLetter(quoteSettings.enableCoverLetter ?? false);
    setShowLocationField(quoteSettings.showLocationField ?? false);
    setAllowItemFieldOverrides(quoteSettings.allowItemFieldOverrides ?? false);
    setItemOverrideFields(quoteSettings.itemOverrideFields);
    setAllowLineUnitOverride(quoteSettings.allowLineUnitOverride ?? false);
    setAllowPerQuoteDocumentEditing(
      quoteSettings.allowPerQuoteDocumentEditing ?? false,
    );
    setDefaultQuoteDocumentTemplateId(
      quoteSettings.defaultQuoteDocumentTemplateId ?? null,
    );
    setCustomFieldsSchema(quoteSettings.customFieldsSchema ?? null);
    setLineItemCustomFieldsSchema(
      quoteSettings.lineItemCustomFieldsSchema ?? null,
    );
    setQuoteNumberPadWidth(quoteSettings.quoteNumberPadWidth ?? 0);
    setQuoteNumberPrefix(quoteSettings.quoteNumberPrefix ?? "");
    setAdvancedSettings(QuoteAdvancedSettingsSchema.parse(quoteSettings));
  }, [quoteSettings]);

  useEffect(() => {
    syncFormFromSettings();
  }, [syncFormFromSettings]);

  // ── Handlers ──

  /**
   * Updates a single advanced setting in the form state.
   * @param key - The setting to update
   * @param value - The setting's new value
   */
  const setAdvancedSetting = <K extends keyof QuoteAdvancedSettings>(
    key: K,
    value: QuoteAdvancedSettings[K],
  ) => {
    setAdvancedSettings((current) => ({ ...current, [key]: value }));
  };

  /**
   * Saves the form, sending Show Location Field only when the admin changed it so the value shown because location is required is never stored.
   * @returns Resolves once the save settles
   */
  const handleSave = async () => {
    setIsSaving(true);
    const isShowLocationFieldChanged =
      showLocationField !== (quoteSettings?.showLocationField ?? false);
    const success = await updateQuoteSettings({
      termsAndConditions,
      paymentTerms,
      deliveryTerms,
      publicQuoteTemplate,
      quotePriceRounding,
      quoteFooter,
      useSignatures,
      showTotals,
      showTotalWeight,
      aggregatePriceListBreaks,
      priceListBreakGroupField,
      showConfidenceScore,
      enableCoverLetter,
      ...(isShowLocationFieldChanged && { showLocationField }),
      allowItemFieldOverrides,
      itemOverrideFields,
      allowLineUnitOverride,
      allowPerQuoteDocumentEditing,
      defaultQuoteDocumentTemplateId,
      customFieldsSchema,
      lineItemCustomFieldsSchema,
      quoteNumberPadWidth: quoteNumberPadWidth > 0 ? quoteNumberPadWidth : null,
      quoteNumberPrefix: quoteNumberPrefix || null,
      ...advancedSettings,
    });
    setIsSaving(false);

    if (success) {
      toast.success("Quote settings saved.");
    } else {
      const quoteError = useQuoteSettingsStore.getState().error;
      toast.error(quoteError || "Failed to save quote settings.");
    }
  };

  const requestSave = async () => {
    const isBreakAggregationChanged =
      aggregatePriceListBreaks !==
        (quoteSettings?.aggregatePriceListBreaks ?? false) ||
      priceListBreakGroupField !==
        (quoteSettings?.priceListBreakGroupField ?? null);
    if (isBreakAggregationChanged) {
      setIsRepriceDialogOpen(true);
      return;
    }
    await handleSave();
  };

  const confirmSave = async () => {
    setIsRepriceDialogOpen(false);
    await handleSave();
  };

  // ── Early returns ──
  if (isLoading) {
    return <QuoteSettingsSkeleton />;
  }

  if (error) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        {/* ── Template & Display ── */}
        <SectionLabel title="Template & Display" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            {!quoteDocumentsEnabled && (
              <SettingsRow
                id="quote-settings-public-template"
                label="Public Quote Template"
                description="Controls the layout used for publicly shared quotes."
              >
                <Select
                  value={publicQuoteTemplate}
                  onValueChange={(value) => {
                    const selected = QUOTE_TEMPLATE_METADATA.find(
                      (template) => template.id === value,
                    );
                    if (selected) {
                      setPublicQuoteTemplate(selected.id);
                    }
                  }}
                  disabled={!isAdmin}
                >
                  <SelectTrigger className="h-auto py-2 w-[860px]">
                    <div className="text-left">
                      <span className="text-sm font-medium">
                        {selectedTemplate?.label || "Select a template"}
                      </span>
                      <Small className="text-muted-foreground block">
                        {selectedTemplate?.description ||
                          "Choose how your public quotes appear."}
                      </Small>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {QUOTE_TEMPLATE_METADATA.map((template) => (
                      <SelectItem
                        key={template.id}
                        value={template.id}
                        className="flex flex-col items-start gap-0.5 py-2"
                      >
                        <span className="text-sm font-medium">
                          {template.label}
                        </span>
                        <Small className="text-muted-foreground">
                          {template.description}
                        </Small>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SettingsRow>
            )}

            {quoteDocumentsEnabled && (
              <SettingsRow
                label="Allow Per-Quote Document Editing"
                description="Let each quote have its own editable document, and give the agent tools to read it."
              >
                <Switch
                  checked={allowPerQuoteDocumentEditing}
                  onCheckedChange={setAllowPerQuoteDocumentEditing}
                  disabled={!isAdmin || isSaving}
                />
              </SettingsRow>
            )}

            {quoteDocumentsEnabled && canEditSettings && (
              <DefaultQuoteTemplatePicker
                value={defaultQuoteDocumentTemplateId}
                onChange={setDefaultQuoteDocumentTemplateId}
                disabled={!isAdmin || isSaving}
              />
            )}

            <SettingsRow
              label="Show Totals"
              description="Display subtotal and total amounts on public quotes."
            >
              <Switch
                checked={showTotals}
                onCheckedChange={setShowTotals}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <QuotePriceRoundingRow
              value={quotePriceRounding}
              onChange={setQuotePriceRounding}
              disabled={!isAdmin || isSaving}
            />

            <SettingsRow
              label="Show Total Weight"
              description="Display total weight on public quotes for items with a unit weight."
            >
              <Switch
                checked={showTotalWeight}
                onCheckedChange={setShowTotalWeight}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              label="Combine quantities for price list breaks"
              description={describeAggregateBreaks(
                aggregatePriceListBreaks,
                effectiveGroupField,
              )}
              expandedContent={
                aggregatePriceListBreaks ? (
                  <PriceListBreakGroupFieldRow
                    value={priceListBreakGroupField}
                    itemFieldNames={groupableItemFieldNames}
                    onChange={setPriceListBreakGroupField}
                    disabled={!isAdmin || isSaving}
                  />
                ) : undefined
              }
            >
              <Switch
                checked={aggregatePriceListBreaks}
                onCheckedChange={setAggregatePriceListBreaks}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              label="Show Confidence Score"
              description="Display AI confidence scores on quote line items in the editor."
            >
              <Switch
                checked={showConfidenceScore}
                onCheckedChange={setShowConfidenceScore}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              label="Show Location Field"
              description="Show location on quote and opportunity editors."
            >
              <div className="flex flex-col items-end gap-1">
                <Switch
                  checked={
                    showLocationField || advancedSettings.requireLocation
                  }
                  onCheckedChange={setShowLocationField}
                  disabled={
                    !isAdmin || isSaving || advancedSettings.requireLocation
                  }
                />
                {advancedSettings.requireLocation && (
                  <Small className="text-muted-foreground">
                    Shown because location is required
                  </Small>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="quote-settings-item-field-overrides"
              label="Allow per line dimensions"
              description="Let a quote line set item measurements for that line only."
              expandedContent={
                allowItemFieldOverrides ? (
                  <ItemOverrideFieldToggles
                    value={itemOverrideFields}
                    onChange={setItemOverrideFields}
                    disabled={!isAdmin || isSaving}
                    showAllowance={enableAllowance}
                  />
                ) : undefined
              }
            >
              <Switch
                checked={allowItemFieldOverrides}
                onCheckedChange={setAllowItemFieldOverrides}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              label="Allow line unit override"
              description="Let a quote line be priced in a unit other than the item unit, for example pieces of an item sold by the pound. Prices convert when the item's measurements or conversion rates allow it; otherwise the line needs a manual unit price."
            >
              <Switch
                checked={allowLineUnitOverride}
                onCheckedChange={setAllowLineUnitOverride}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              id="quote-settings-require-esignature"
              label="Require E-Signature"
              description="New finalized quotes will require a customer e-signature by default."
            >
              <Switch
                checked={useSignatures}
                onCheckedChange={setUseSignatures}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>

            <SettingsRow
              id="quote-settings-number-prefix"
              label="Quote Number Prefix"
              description='Adds a prefix to quote display numbers (e.g. "QA" → "QA000001").'
            >
              <div className="flex flex-col items-end gap-1">
                <Input
                  type="text"
                  maxLength={10}
                  placeholder="e.g. QA"
                  value={quoteNumberPrefix}
                  onChange={(e) => setQuoteNumberPrefix(e.target.value)}
                  disabled={!isAdmin || isSaving}
                  className="w-24"
                />
              </div>
            </SettingsRow>

            <SettingsRow
              label="Quote Number Padding"
              description='Pads quote numbers with leading zeros for display (e.g. "0001"). Set to 0 to disable.'
            >
              <div className="flex flex-col items-end gap-1">
                <Input
                  type="number"
                  min={0}
                  max={10}
                  step={1}
                  value={quoteNumberPadWidth}
                  onChange={(e) => {
                    const next = Number.parseInt(e.target.value, 10);
                    if (Number.isNaN(next)) {
                      setQuoteNumberPadWidth(0);
                      return;
                    }
                    setQuoteNumberPadWidth(Math.min(10, Math.max(0, next)));
                  }}
                  disabled={!isAdmin || isSaving}
                  className="w-24"
                />
                <Small className="text-muted-foreground">
                  Preview: Quote 1 will display as &quot;
                  {formatQuoteNumber(
                    1,
                    quoteNumberPadWidth,
                    quoteNumberPrefix || null,
                  )}
                  &quot;
                </Small>
              </div>
            </SettingsRow>

            <SettingsRow
              label="Enable Cover Letters"
              description="Allow adding cover letters to quotes."
            >
              <Switch
                checked={enableCoverLetter}
                onCheckedChange={setEnableCoverLetter}
                disabled={!isAdmin || isSaving}
              />
            </SettingsRow>
          </div>
        </Card>

        {/* ── Default Terms ── */}
        <SectionLabel title="Default Terms" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="quote-settings-terms-conditions"
              label="Terms and Conditions"
              description="Appears at the bottom of all quotes."
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator
                  configured={termsAndConditions.length > 0}
                />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTermsSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="quote-settings-payment-terms"
              label="Payment Terms"
              description='E.g. "Net 30", "50% deposit, remainder on delivery".'
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={paymentTerms.length > 0} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaymentSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="quote-settings-delivery-terms"
              label="Delivery Terms"
              description='E.g. "FOB Origin", "Delivery within 14 days of order".'
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={deliveryTerms.length > 0} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeliverySheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="quote-settings-footer"
              label="Quote Footer"
              description="Disclaimer text at the bottom of all public quotes."
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={quoteFooter.length > 0} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFooterSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>
          </div>
        </Card>

        {/* ── Custom Fields ── */}
        <SectionLabel title="Custom Fields" />
        <Card className="shadow-sm overflow-hidden">
          <div className="px-4 py-3.5">
            <p className="text-sm font-medium mb-1">Quote custom fields</p>
            <p className="text-xs text-muted-foreground mb-3">
              Define custom metadata fields that appear on the quote itself.
              Custom fields are included as additional columns in CSV exports.
            </p>
            <CustomFieldsSchemaEditor
              schema={customFieldsSchema}
              onChange={setCustomFieldsSchema}
              disabled={!isAdmin || isSaving}
            />
          </div>
          <div className="border-t px-4 py-3.5">
            <p className="text-sm font-medium mb-1">Line item custom fields</p>
            <p className="text-xs text-muted-foreground mb-3">
              Define custom metadata fields that appear on each individual quote
              line item.
            </p>
            <CustomFieldsSchemaEditor
              schema={lineItemCustomFieldsSchema}
              onChange={setLineItemCustomFieldsSchema}
              disabled={!isAdmin || isSaving}
            />
          </div>
        </Card>

        {/* ── Advanced Settings ── */}
        <AdvancedSettingsSection
          anchorIdPrefix="quote-settings-advanced"
          hasLockedSetting={Object.keys(lockedBy).length > 0}
        >
          <SettingsRow
            id="quote-settings-advanced-require-location"
            label="Require Location"
            description="Every quote must have a location. Also turns on Show Location Field."
            lockedBy={lockedBy.requireLocation}
          >
            <Switch
              checked={advancedSettings.requireLocation}
              onCheckedChange={(checked) =>
                setAdvancedSetting("requireLocation", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.requireLocation}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-require-ship-to"
            label="Require Ship-To"
            description="Every quote must have a ship-to address."
            lockedBy={lockedBy.requireShipTo}
          >
            <Switch
              checked={advancedSettings.requireShipTo}
              onCheckedChange={(checked) =>
                setAdvancedSetting("requireShipTo", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.requireShipTo}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-require-contact"
            label="Require Contact"
            description="Every quote must have a customer contact."
          >
            <Switch
              checked={advancedSettings.requireContact}
              onCheckedChange={(checked) =>
                setAdvancedSetting("requireContact", checked)
              }
              disabled={!isAdmin || isSaving}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-require-length-and-width"
            label="Require Length and Width"
            description="Lines on items priced by weight need a length and width (area items) or a length (linear items) before the quote can be finalized."
            lockedBy={lockedBy.requireLengthAndWidth}
          >
            <Switch
              checked={advancedSettings.requireLengthAndWidth}
              onCheckedChange={(checked) =>
                setAdvancedSetting("requireLengthAndWidth", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.requireLengthAndWidth
              }
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-require-line-item"
            label="Require an Item on Every Line"
            description="Every quote line must reference an item. Section, note, and discount lines are exempt."
            lockedBy={lockedBy.requireLineItem}
          >
            <Switch
              checked={advancedSettings.requireLineItem}
              onCheckedChange={(checked) =>
                setAdvancedSetting("requireLineItem", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.requireLineItem}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-max-quantity-breaks"
            label="Maximum Quantity Breaks per Line"
            description="Limits how many quantity breaks a quote line can have. Leave empty for no limit."
            lockedBy={lockedBy.maxQuantityBreaksPerLine}
          >
            <SettingLimitInput
              value={advancedSettings.maxQuantityBreaksPerLine}
              onChange={(value) =>
                setAdvancedSetting("maxQuantityBreaksPerLine", value)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.maxQuantityBreaksPerLine
              }
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-allow-fractional-quantities"
            label="Allow Fractional Quantities"
            description="Let quote line quantities include decimals. Turn off to allow whole numbers only."
            lockedBy={lockedBy.allowFractionalQuantities}
          >
            <Switch
              checked={advancedSettings.allowFractionalQuantities}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowFractionalQuantities", checked)
              }
              disabled={
                !isAdmin || isSaving || !!lockedBy.allowFractionalQuantities
              }
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-max-quote-number-length"
            label="Maximum Quote Number Length"
            description="Limits how many characters a quote number can have. Leave empty for no limit."
            lockedBy={lockedBy.maxQuoteNumberLength}
          >
            <SettingLimitInput
              value={advancedSettings.maxQuoteNumberLength}
              onChange={(value) =>
                setAdvancedSetting("maxQuoteNumberLength", value)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.maxQuoteNumberLength}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-allow-discount-lines"
            label="Allow Discount Lines"
            description="Let quotes include discount lines."
            lockedBy={lockedBy.allowDiscountLines}
          >
            <Switch
              checked={advancedSettings.allowDiscountLines}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowDiscountLines", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.allowDiscountLines}
            />
          </SettingsRow>

          <SettingsRow
            id="quote-settings-advanced-allow-duplicate-items"
            label="Allow Duplicate Items"
            description="Let the same item appear on more than one line of a quote."
            lockedBy={lockedBy.allowDuplicateItems}
          >
            <Switch
              checked={advancedSettings.allowDuplicateItems}
              onCheckedChange={(checked) =>
                setAdvancedSetting("allowDuplicateItems", checked)
              }
              disabled={!isAdmin || isSaving || !!lockedBy.allowDuplicateItems}
            />
          </SettingsRow>
        </AdvancedSettingsSection>

        <UnsavedChangesBar
          isDirty={isAdmin && isDirty}
          onSave={requestSave}
          onDiscard={syncFormFromSettings}
          isSaving={isSaving}
        />
      </div>

      <PriceBreakAggregationChangeDialog
        open={isRepriceDialogOpen}
        onCancel={() => setIsRepriceDialogOpen(false)}
        onConfirm={confirmSave}
      />

      {/* ── Sheets ── */}
      <TextSettingSheet
        open={termsSheetOpen}
        onOpenChange={setTermsSheetOpen}
        title="Terms and Conditions"
        description="Default terms and conditions that appear at the bottom of all quotes."
        value={termsAndConditions}
        onApply={setTermsAndConditions}
        placeholder="Enter default terms and conditions..."
      />

      <TextSettingSheet
        open={paymentSheetOpen}
        onOpenChange={setPaymentSheetOpen}
        title="Payment Terms"
        description="Default payment terms included on quotes."
        value={paymentTerms}
        onApply={setPaymentTerms}
        placeholder='E.g. "Net 30", "50% deposit, remainder on delivery"'
      />

      <TextSettingSheet
        open={deliverySheetOpen}
        onOpenChange={setDeliverySheetOpen}
        title="Delivery Terms"
        description="Default delivery terms included on quotes."
        value={deliveryTerms}
        onApply={setDeliveryTerms}
        placeholder='E.g. "FOB Origin", "Delivery within 14 days of order"'
      />

      <TextSettingSheet
        open={footerSheetOpen}
        onOpenChange={setFooterSheetOpen}
        title="Quote Footer"
        description="Disclaimer text that appears at the bottom of all public quotes."
        value={quoteFooter}
        onApply={setQuoteFooter}
        placeholder="Enter disclaimer text..."
      />
    </>
  );
}

// ── Local Components ──

/**
 * Describes how combined price list breaks pick each line's break.
 * @param aggregate - Whether line quantities are combined
 * @param groupField - The item custom field lines are grouped by, if any
 * @returns The setting description
 */
function describeAggregateBreaks(
  aggregate: boolean,
  groupField: string | undefined,
): string {
  if (!aggregate || !groupField) {
    return "Pick each line's price list break from the total quantity of that item across the quote.";
  }
  return `Pick each line's price list break from the total quantity of all items with the same ${groupField} across the quote.`;
}

/**
 * Skeleton loading state matching the QuoteSettingsPanel layout.
 * @returns Skeleton layout element
 */
function QuoteSettingsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-4 w-28" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={`display-${i}`} />
          ))}
        </div>
      </Card>

      <Skeleton className="h-4 w-24" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonRow key={`terms-${i}`} />
          ))}
        </div>
      </Card>

      <Skeleton className="h-4 w-24" />
      <Card className="shadow-sm overflow-hidden">
        <div className="px-4 py-3.5">
          <Skeleton className="h-3 w-56 mb-3" />
          <Skeleton className="h-8 w-full" />
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-[200px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

/**
 * Small status indicator showing whether a text-based setting has been configured.
 * @param props - Contains a configured boolean
 * @returns Status indicator element
 */
function ConfiguredIndicator({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Configured
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground/50">Not set</span>;
}
